import React, { useRef, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Editor } from '@tinymce/tinymce-react';
import Axios from 'axios';
import { URL_LIST_ALL } from '../configs/api';
import { useSelector } from 'react-redux';
import Spinner from '../component/Spinner';
import { confirmAlert } from 'react-confirm-alert';

export default function EditPostModal({ post, onClose, onUpdated, listGroup }) {
    const editorRef = useRef(null);
    const { user } = useSelector(state => state.auth);
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [imagePreviews, setImagePreviews] = useState([]);

    const [groupId, setGroupId] = useState(post && post.group_id)
    const [isVote, setIsVote] = useState(post && (post.post_type == 'single_vote' || post.post_type == 'multiple_vote') ? true : false)
    const [listVote, setListVote] = useState(post && post.post_meta ? post.post_meta : [])
    const [metaTitle, setMetaTitle] = useState(null)
    const [voteType, setVoteType] = useState(post && post.post_type != 'single_vote' ? 'multiple' : 'single')
    const [votePrivate, setVotePrivate] = useState(post && post.vote_private ? true : false)
    const [voteExpired, setVoteExpired] = useState(post && post.vote_expired)

    useEffect(() => {
        // Nếu post.list_images là chuỗi JSON, parse nó
        if (post.list_images) {
            const imgs = typeof post.list_images === 'string' ? JSON.parse(post.list_images) : post.list_images;
            setImagePreviews(imgs || []);
        }

    }, [post]);

    const handleUploadImage = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const resizedBlob = await convertImageToBlobAfterResize(file);
            const formData = new FormData();
            formData.append('file', resizedBlob, file.name); // giữ tên gốc
            formData.append('changename', 1);
            formData.append('folder', 0);

            const res = await Axios.post(URL_LIST_ALL + 'fileupload', formData, user.config);
            if (res.data.status === 'success') {
                const imgURL = res.data.message;
                const imgTag = `<img src="${imgURL}" alt="uploaded image" />`;
                if (editorRef.current) {
                    editorRef.current.insertContent(imgTag);
                }
            } else {
                alert("Upload thất bại: " + res.data.message);
            }
        } catch (err) {
            console.error("Lỗi upload ảnh:", err);
            alert("Lỗi upload ảnh");
        }
    };

    const convertImageToBlobAfterResize = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    const maxSize = 2048;
                    const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
                    const canvas = document.createElement("canvas");
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    // Chuyển base64 sang blob
                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                resolve(blob);
                            } else {
                                reject(new Error("Không thể tạo blob từ canvas"));
                            }
                        },
                        "image/jpeg",
                        1
                    );
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    const handleMultiImageUpload = async (e) => {
        const files = e.target.files;
        if (!files) return;

        const uploadPromises = [...files].map(file => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('changename', 1);
            formData.append('folder', 0);

            setUploading(true);
            return Axios.post(URL_LIST_ALL + 'fileupload', formData, user.config).then(res => {
                if (res.data.status === 'success') {
                    return res.data.message;
                } else {
                    confirmAlert({
                        title: 'Lỗi',
                        message: res.data.message,
                        buttons: [{ label: 'OK' }]
                    });
                    return null;
                }
            }).catch(err => {
                console.error(err);
                return null;
            });
        });

        const uploadedImages = await Promise.all(uploadPromises);
        const filteredImages = uploadedImages.filter(url => url !== null);
        setImagePreviews(prev => [...prev, ...filteredImages]);
        setUploading(false);
    };

    const handleRemoveImage = (index) => {
        confirmAlert({
            title: 'Xác nhận',
            message: 'Bạn có chắc muốn xóa ảnh này?',
            buttons: [
                {
                    label: 'Có',
                    onClick: () => {
                        const updated = [...imagePreviews];
                        updated.splice(index, 1);
                        setImagePreviews(updated);
                    }
                },
                {
                    label: 'Không'
                }
            ]
        });
    };

    const handleSubmit = () => {
        const content = editorRef.current?.getContent();
        if (!content || content.trim() === '') {
            alert('Vui lòng nhập nội dung');
            return;
        }

        setLoading(true);
        Axios.put(`${URL_LIST_ALL}posts/${post.id}`, {
            post_content: content,
            list_images: imagePreviews,
            group_id: groupId,
            vote_expired: voteExpired,
            post_type: isVote ? (voteType == "single" ? "single_vote" : "multiple_vote") : null,
            post_meta: isVote && listVote && listVote.length > 0 ? listVote : null
        }, user.config).then(res => {
            if (res.data.status === 'success') {
                onUpdated?.(res.data.data);
                onClose();
            } else {
                alert(res.data.message);
            }
        }).catch(err => {
            console.error(err);
            alert('Lỗi cập nhật');
        }).finally(() => {
            setLoading(false);
        });
    };

    const addNewMeta = () => {
        if (metaTitle.trim() === "") return; // không thêm nếu trống
        setListVote(prev => [...prev, metaTitle.trim()]);
        setMetaTitle(""); // reset input
    }

    const removeVote = (indexToRemove) => {
        setListVote(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleEditVote = (index, value) => {
        const updated = [...listVote];
        updated[index] = value;
        setListVote(updated);
    };

    return (
        <Modal show onHide={onClose} size="xl" enforceFocus={false}>
            <Modal.Header closeButton>
                <Modal.Title>Chỉnh sửa bài viết</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='w-100 modal-body-post'>
                    <Editor
                        apiKey="8vzg9hkuturhxampptav8epspf6gw56ny951lc2o70rehku4"
                        onInit={(evt, editor) => editorRef.current = editor}
                        initialValue={post.post_content}
                        init={{
                            height: 300,
                            menubar: false,
                            plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount', 'emoticons', 'permanentpen'
                            ],
                            toolbar: 'undo redo | blocks | ' +
                                'bold italic forecolor | alignleft aligncenter | code | link ' +
                                'alignright alignjustify | bullist numlist outdent indent | ' +
                                'removeformat | emoticons charmap | image | permanentpen',
                        }}
                    />
                    <div className="upload-image-post">
                        <button className="btn btn-outline-info" onClick={() => fileInputRef.current.click()}>
                            <i className='fas fa-camera-retro'></i>
                        </button>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleUploadImage}
                        />
                    </div>
                    <div className="row mt-3">
                        <div className='col-12 col-sm-6'>
                            <label className="btn btn-secondary">
                                Tải lên hình ảnh
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={handleMultiImageUpload}
                                />
                            </label>

                            {uploading && <Spinner />}
                        </div>
                        {imagePreviews.length > 0 && (
                            <div className="mt-3 d-flex flex-wrap gap-2">
                                {imagePreviews.map((src, index) => (
                                    <div key={index} style={{ position: 'relative' }}>
                                        <img
                                            src={src}
                                            alt={`preview-${index}`}
                                            style={{
                                                width: 100,
                                                height: 100,
                                                objectFit: 'cover',
                                                borderRadius: 8
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            style={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                padding: '2px 6px',
                                                fontSize: 10,
                                                borderRadius: '50%'
                                            }}
                                            onClick={() => handleRemoveImage(index)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className='col-12 col-sm-4 form-group'>
                            <select className="form-select height32" onChange={(e) => setGroupId(e.target.value)} value={groupId}>
                                <option value={null}>Đăng lên tất cả</option>
                                {listGroup && listGroup.map((gr, j) => {
                                    return <option key={j} value={gr.id}>{gr.title}</option>
                                })}
                            </select>
                        </div>

                        <div className='col-12 col-sm-3 form-group'>
                            <button className={"btn btn-sm"} type='button' onClick={() => setIsVote(!isVote)}>
                                <i className={isVote ? "fas fa-check-circle" : "far fa-circle"}></i> Tạo bình chọn
                            </button>
                        </div>

                        {isVote &&
                            <>
                                <div className='col-12 col-sm-3 form-group'>
                                    <button className={"btn btn-sm"} type='button' onClick={() => setVoteType(voteType != "single" ? "single" : "multiple")}>
                                        <i className={voteType != "single" ? "fas fa-check-circle" : "far fa-circle"}></i> Cho phép chọn nhiều lựa chọn
                                    </button>
                                </div>
                                <div className='col-12 col-sm-3 form-group'>
                                    <button className={"btn btn-sm"} type='button' onClick={() => setVotePrivate(!votePrivate)}>
                                        <i className={votePrivate ? "fas fa-check-circle" : "far fa-circle"}></i> Cho phép bình chọn ẩn danh
                                    </button>
                                </div>
                                <div className='col-12 col-sm-3 form-group'>
                                    <input className='form-control height32' type='text' name='vote_expired' placeholder='Thời hạn. VD: 12:00 27/08/2025' value={voteExpired} onChange={(e)=>setVoteExpired(e.target.value)} />
                                </div>
                                <div className='col-12'></div>
                                <div className='col-12 col-sm-6'>
                                    <div className="input-group mb-3">
                                        <input type="text" className="form-control height32" placeholder="Lựa chọn" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} />
                                        <div className="input-group-prepend">
                                            <button type="button" className="btn btn-sm btn-success height32" onClick={() => addNewMeta()}>{loading ? <Spinner /> : 'Thêm'}</button>
                                        </div>
                                    </div>
                                </div>
                                <div className='col-12'></div>
                                {listVote && Array.isArray(listVote) && listVote.map((iv, c) => <div className=' col-12 col-sm-6  mt-3' key={c}>
                                    <div className='vote-item input-group'>
                                        <input type='text' className='form-control height32' value={iv} name={"vote-item-" + c} onChange={(e) => handleEditVote(c, e.target.value.replace(/^(\d+\.\s)?/, ''))} />
                                        <div className="input-group-prepend">
                                            <button className='btn btn-sm btn-danger btn-trash' type='button' onClick={() => removeVote(c)}><span className='fas fa-trash-alt'></span></button>
                                        </div>
                                    </div>
                                </div>
                                )}
                            </>
                        }
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={onClose}>Hủy</button>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                    Cập nhật
                </button>
            </Modal.Footer>
        </Modal>
    );
}
